import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getExpandedRowModel } from '@tanstack/react-table';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDataTable } from '@/hooks/use-data-table';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { TableIds } from '@/lib/constants/table-ids';
import { DateRangePresetCategory } from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { DEFAULT_CURRENCY_CODE } from '@/modules/common/constants/currency.constants';
import { useOrganizationDefaults } from '@/modules/common/hooks';
import {
  GenerateEntityTemplate,
  useGenerateEntityPage,
  useGenerateRefresh,
  useGenerateSelectionState,
} from '@/modules/finance/common';
import { AddCustomEntityButton } from '@/modules/finance/common/components/add-custom-entity-button';
import { getShortProjects } from '@/modules/projects';
import { useCustomAttributes } from '@/modules/team/hooks/use-custom-attributes';
import { AuthenticatedRoutes } from '@/router/routes';

import { GenerateBillsFooter, GenerateBillsModal, MonthlySalaryBulkToggle } from '../components';
import {
  canSelectBillRow,
  getBillRowId,
} from '../components/generate-bills-table/bill-row.helpers';
import {
  BILLS_FILTER_KEY_BILLING_PERIOD,
  BILLS_FILTER_KEY_MEMBER,
  BILLS_FILTER_KEY_PROJECT,
  BILLS_FILTER_KEY_SEARCH,
  BILLS_TABLE_FILTER_KEYS,
  GENERATE_BILLS_TABLE_FILTERS_STORAGE_KEY,
  getGenerateBillsFiltersConfig,
  getGenerateBillsTableColumns,
} from '../constants';
import {
  mapGenerateBillsDataFilters,
  useBillCustomMembers,
  useBillCustomRows,
  useBillMembersPayload,
  useBillTableData,
  useGenerateBillsData,
  useGenerateBillsSmartRefresh,
  useMonthlySalaryMode,
  useMonthlySalaryOverrides,
} from '../hooks';
import { GenerateBillListItem } from '../types';
import { GenerateBillsTableFormValues, generateBillsValidationSchema } from '../validations';

// Year/Quarter presets are hidden here because this screen loads the whole range
// eagerly and the backend currently fails on year/quarter-sized datasets. Restore
// them once this screen moves to the lazyload approach.
// TODO(lazyload): re-enable Year/Quarter presets when generate is paginated.
const CUSTOM_MEMBER_PICKER_QUERY_KEY = ['generate-bills-custom-member-options'];

const GENERATE_HIDDEN_PRESET_CATEGORIES = [
  DateRangePresetCategory.Year,
  DateRangePresetCategory.Quarter,
];

export const GenerateBillsPage = () => {
  const { t } = useTranslation(['authenticated.bills', 'common'], { keyPrefix: 'new' });
  const { t: tCommon } = useTranslation('common');

  const navigate = useNavigate();
  const navigateToBills = () => navigate(AuthenticatedRoutes.Bills);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const { data: customAttributes } = useCustomAttributes();
  const filtersConfig = useMemo(
    () => getGenerateBillsFiltersConfig(customAttributes),
    [customAttributes]
  );

  // Custom rows and salary-mode overrides are declared before `page` because their dirty flags
  // feed the unsaved-changes guard, which reads `additionalIsDirty` during render. Both are
  // handed a ref-backed form accessor so they don't depend on `page.form` existing yet.
  const formRef = useRef<UseFormReturn<GenerateBillsTableFormValues> | null>(null);
  const unregisterFields = useCallback(
    (path: string) => formRef.current?.unregister(path as never),
    []
  );

  const customRows = useBillCustomRows({ unregisterFields });
  const customMembers = useBillCustomMembers();
  const salaryOverrides = useMonthlySalaryOverrides();

  const { data: organizationDefaults } = useOrganizationDefaults();
  const defaultCurrencyCode = organizationDefaults?.currency.code ?? DEFAULT_CURRENCY_CODE;

  // `page` owns filters / form / URL state; the hooks around it own this screen's own concerns.
  const page = useGenerateEntityPage<GenerateBillsTableFormValues>({
    filterKeys: {
      billingPeriod: BILLS_FILTER_KEY_BILLING_PERIOD,
      search: BILLS_FILTER_KEY_SEARCH,
      arrayKeys: [BILLS_FILTER_KEY_MEMBER, BILLS_FILTER_KEY_PROJECT],
    },
    filtersStorageKey: GENERATE_BILLS_TABLE_FILTERS_STORAGE_KEY,
    filterKeysList: BILLS_TABLE_FILTER_KEYS,
    filtersConfig,
    formSchema: generateBillsValidationSchema,
    unsavedChangesEntityLabel: t('common.bills'),
    additionalIsDirty:
      customRows.hasCustomRows || customMembers.hasCustomMembers || salaryOverrides.hasOverrides,
    onDiscardAdditional: () => {
      customRows.reset();
      customMembers.reset();
      salaryOverrides.reset();
    },
  });
  formRef.current = page.form;

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    dataUpdatedAt,
    fetchNextPage,
  } = useGenerateBillsData({ filters: page.appliedFilters });

  const monthlySalary = useMonthlySalaryMode({
    overrides: salaryOverrides,
    members: data?.items,
  });

  const allMemberIds = useMemo(() => {
    const ids = new Set(customMembers.customMemberIds);
    if (data?.items) {
      for (const item of data.items) {
        ids.add(item.member.id);
      }
    }
    return ids;
  }, [data?.items, customMembers.customMemberIds]);

  const apiFilters = useMemo(
    () => mapGenerateBillsDataFilters(page.appliedFilters),
    [page.appliedFilters]
  );

  const { hasStaleData, isRefreshing, applySmartRefresh } = useGenerateBillsSmartRefresh({
    filters: page.appliedFilters,
    apiFilters,
    enabled: page.isBillingPeriodSelected,
    items: data?.items,
  });

  // Paginated server-side loaders for the "Add custom project" picker. Each picker instance
  // shares the same TanStack-Query cache (same queryKey), so opening multiple custom-project
  // rows on the page doesn't multiply requests.
  const addedProjectSelector = useMemo(
    () => ({
      loadItems: (cursor?: number | null) =>
        getShortProjects({ pagination: { cursor, pageSize: DEFAULT_PAGE_SIZE } }),
      searchItems: (searchTerm: string, cursor?: number | null) =>
        getShortProjects({
          filter: { searchString: searchTerm },
          pagination: { cursor, pageSize: DEFAULT_PAGE_SIZE },
        }),
    }),
    []
  );

  const columns = useMemo(
    () =>
      getGenerateBillsTableColumns({
        form: page.form,
        overheadActions: customRows.overheadActions,
        addedProjectActions: customRows.addedProjectActions,
        addedProjectSelector,
        monthlySalaryActions: monthlySalary.actions,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      customRows.overheadActions,
      customRows.addedProjectActions,
      addedProjectSelector,
      monthlySalary.actions,
    ]
  );

  const tableData = useBillTableData({
    members: data?.items,
    customMembers: customMembers.customMembers,
    memberOverheads: customRows.memberOverheads,
    memberAddedProjects: customRows.memberAddedProjects,
    isMonthlySalaryEnabledFor: monthlySalary.isEnabledFor,
  });

  const { table, saveColumnSizing } = useDataTable<GenerateBillListItem>({
    data: tableData,
    columns,
    getRowId: getBillRowId,
    enableRowSelection: canSelectBillRow,
    getExpandedRowModel: getExpandedRowModel(),
    // @ts-expect-error memberSubRows model and row.original aren't same
    getSubRows: row => row.memberSubRows,
    initialState: { columnPinning: { left: ['select', 'memberAndProject'] } },
    tableId: TableIds.GENERATE_BILLS,
  });

  // A newly added custom row isn't in TanStack's model until `tableData` rebuilds, so the
  // selection is flushed here rather than inside the add callbacks.
  useEffect(() => {
    customRows.flushPendingSelections(table);
    customMembers.flushPendingRows(table);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, table]);

  const { refreshButtonProps } = useGenerateRefresh({
    form: page.form,
    table,
    hasStaleData,
    isRefreshing,
    isFetching,
    dataUpdatedAt,
    applySmartRefresh,
  });

  const { selectedRowObjects, selectedRowsCount, hasTableRows, pulseContainerRef, clearSelection } =
    useGenerateSelectionState({ table, itemsCount: data?.items?.length ?? 0 });

  const getBillMembers = useBillMembersPayload({ form: page.form, table, selectedRowObjects });

  const modals = useMemo(
    () => (
      <GenerateBillsModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        billingPeriodLabel={page.billingPeriodLabel}
        billingPeriod={page.billingPeriodValue}
        billMembers={getBillMembers()}
        onSuccess={page.handleGenerateSuccess}
      />
    ),
    [
      isConfirmModalOpen,
      page.billingPeriodLabel,
      page.billingPeriodValue,
      page.handleGenerateSuccess,
      getBillMembers,
    ]
  );

  return (
    <GenerateEntityTemplate<GenerateBillListItem, GenerateBillsTableFormValues>
      table={table}
      form={page.form}
      error={error}
      showDatePickerOnly={!page.isBillingPeriodSelected}
      datePickerPrompt={t('datePickerPrompt')}
      dates={page.dates}
      selectedPreset={page.selectedPreset}
      onDateChange={page.handleDateChange}
      onPresetSelect={page.handlePresetSelect}
      onDateReset={page.handleDateReset}
      dateRangeLabel={t('filters.label')}
      dateRangeInfoTooltip={t('filters.dateRangeInfo')}
      hiddenPresetCategories={GENERATE_HIDDEN_PRESET_CATEGORIES}
      filtersConfig={filtersConfig}
      filterState={page.filterState}
      refreshButtonProps={refreshButtonProps}
      hasMore={hasNextPage}
      isLoading={isLoading || isFetchingNextPage}
      onLoadMore={fetchNextPage}
      displayTitle={t('title')}
      onBack={navigateToBills}
      entityCounter={t(`entityCounter.${page.hasSearch ? 'withSearch' : 'common'}`, {
        count: data?.pagination.total ?? 0,
      })}
      totalCount={data?.pagination.total ?? 0}
      isAnyFilterApplied={page.isAnyFilterApplied}
      selectedItemsCounter={t('selectedItemsCounter', { count: selectedRowsCount })}
      selectedItemsCount={selectedRowsCount}
      hasTableRows={hasTableRows}
      pulseContainerRef={pulseContainerRef}
      toolbarSlot={
        <MonthlySalaryBulkToggle
          eligibleCount={monthlySalary.eligibleMemberIds.length}
          isAllEnabled={monthlySalary.isAllEnabled}
          onToggle={monthlySalary.toggleAll}
        />
      }
      filtersActionSlot={
        <AddCustomEntityButton
          buttonLabel={tCommon('generateEntities.addCustomMember.button')}
          buttonTooltip={tCommon('generateEntities.addCustomMember.tooltip')}
          searchPlaceholder={tCommon('generateEntities.addCustomMember.searchPlaceholder')}
          emptyMessage={tCommon('generateEntities.addCustomMember.emptyMessage')}
          allExcludedMessage={tCommon('generateEntities.addCustomMember.allAddedMessage')}
          excludeIds={allMemberIds}
          queryKey={CUSTOM_MEMBER_PICKER_QUERY_KEY}
          fetchItems={customMembers.fetchMembers}
          onSelect={member => customMembers.addCustomMember(member, defaultCurrencyCode)}
          renderItem={item => (
            <span className="flex items-center gap-2">
              <span className="font-medium">{item.name}</span>
              {item.email && <span className="text-muted-foreground">{item.email}</span>}
            </span>
          )}
          getItemLabel={item => item.name}
        />
      }
      selectionHint={tCommon('generateEntities.selectionHintBills')}
      onClearClick={clearSelection}
      onGenerateClick={() => setIsConfirmModalOpen(true)}
      modals={modals}
      submitButtonLabel={t('buttons.createInvoices')}
      footerSlot={
        <GenerateBillsFooter
          form={page.form}
          table={table}
          onClearClick={clearSelection}
          onGenerateClick={() => setIsConfirmModalOpen(true)}
          selectedItemsCounter={t('selectedItemsCounter', { count: selectedRowsCount })}
          selectedItemsCount={selectedRowsCount}
          submitButtonLabel={t('buttons.createInvoices')}
        />
      }
      unsavedChangesGuard={page.unsavedChangesGuardProps}
      tableId={TableIds.GENERATE_BILLS}
      onResizeEnd={saveColumnSizing}
    />
  );
};
